import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isValidAdminUser } from './authConfig';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import PolicyGapReport from './pages/PolicyGapReport';
import Deployments from './pages/Deployments';
import Policies from './pages/Policies';
import PolicyMaintenance from './pages/PolicyMaintenance';
import FeatureCatalog from './pages/FeatureCatalog'; // This remains for managing feature keys
import PlanCatalogPage from './pages/PlanCatalogPage'; // New page for managing plans
import PlanMappingsPage from './pages/PlanMappingsPage'; // New page for mapping plans to features
import SystemHealth from './pages/SystemHealth';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import config from './config';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
});

function AuthChecker() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user is authenticated, validate they're from allowed domain
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      if (!isValidAdminUser(account.username, account.tenantId)) {
        // User authenticated but not from allowed domain
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        alert('Access denied. Admin portal requires a tc-config.com or technologyconsulting.llc email address.');
        navigate('/login');
      }
    }
  }, [isAuthenticated, accounts, navigate]);

  return null;
}

function App() {
  const { accounts } = useMsal();
  const isDevelopment = config.isDevelopment;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthChecker />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetails />} />
            <Route path="customers/:id/gap-assessment" element={<PolicyGapReport />} />
            <Route path="deployments" element={<Deployments />} />
            <Route path="policies" element={<Policies />} />
            <Route path="policy-maintenance" element={<PolicyMaintenance />} />
            <Route path="feature-catalog" element={<FeatureCatalog />} />
            <Route path="plan-catalog" element={<PlanCatalogPage />} />
            <Route path="plan-mappings" element={<PlanMappingsPage />} />
            <Route path="system-health" element={<SystemHealth />} />
            <Route path="system-health" element={<div>System Health Page (Coming Soon)</div>} />
            <Route path="settings" element={<div>Settings Page (Coming Soon)</div>} />

          </Route>

          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
